import {
  DEMO_LINK_DEFAULT_FOR_CLIENT,
  GiveADemoFlowModal,
  getAssetType,
  getEntityTitle,
  getEntityType,
} from '@carboncopyco/components';
import {
  selectOrganizationId,
  useCreatePlayMutation,
  useLazyGetNotesQuery,
  useLazyGetPresentationQuery,
} from '@carboncopyco/data';
import type {
  DemoCreationData,
  DemoOverlay,
  IDemo,
  NoteEntityType,
  Play,
  Playbook,
  PlaybookAsset,
  Tour,
  TourCreationData,
} from '@carboncopyco/types';
import { PresentationTypeConst } from '@carboncopyco/types/desktop';
import { notify } from '@carboncopyco/ui';
import { AnalyticsTrackEventName, useAnalytics } from '@carboncopyco/utils';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { settings } from '../../../../settings';
import { usePlaybookPresentations } from '../../hooks/usePlaybookPresentations';
import { useTabManager } from '../../services/TabManager';
import { usePresentationService } from '../../services/presenter/PresentationService';
import {
  type PresentationData,
  preparePresentationsData,
} from '../../shared/utils/preparePresentationsData';
import ModalWrapper from '../Modals/ModalWrapper';

type Entity = IDemo | DemoOverlay | Tour | Playbook;

interface GiveADemoModalExtendedProps {
  open: boolean;
  entity: Entity;
  onClose?: () => void;
  onPresent?: (presentationData?: PresentationData) => void;
  onSave?: (presentation: Play) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  onBeforePresent?: () => void;
  withNotificationOnPresent?: boolean;
  assetRevisionId?: string;
  withGiveADemo?: boolean;
  hidePlayLater?: boolean;
  workspaceId: string;
}

export const GiveADemoModalExtended = ({
  open,
  entity,
  onClose,
  onPresent,
  onSave,
  onLoadingChange,
  onBeforePresent,
  withNotificationOnPresent = false,
  assetRevisionId,
  withGiveADemo = true,
  hidePlayLater,
  workspaceId,
}: GiveADemoModalExtendedProps) => {
  const { trackEvent } = useAnalytics();
  const organizationId = useSelector(selectOrganizationId);
  const { openDemoPresentation } = usePresentationService();
  const { closeGroupTabs, getActiveGlobalTab } = useTabManager();
  const [getPresentation] = useLazyGetPresentationQuery();
  const { handlePlayPlaybook } = usePlaybookPresentations({
    organizationId,
  });

  const [createPlay] = useCreatePlayMutation();
  const [getNotes] = useLazyGetNotesQuery();

  const isPlaybook = getAssetType(entity) === 'playbook';

  const getAssets = () => {
    let assets;
    if (isPlaybook && 'assets' in entity) {
      assets = entity.assets;
    } else {
      assets = [
        {
          assetId: String(entity.id),
          assetRevisionId: Number(assetRevisionId ?? entity.revisionId),
          assetType: getEntityType(entity as IDemo | DemoOverlay | Tour),
          autoSync: false,
          entity,
        } as PlaybookAsset,
      ];
    }
    return assets;
  };

  const handleOnPresent = async (data: Play) => {
    onBeforePresent?.();
    trackEvent(AnalyticsTrackEventName.DEMO_CLICK_START_PRESENTATION, {
      location: 'give_a_demo_modal',
    });

    if (isPlaybook) {
      onPresent?.();
      handlePlayPlaybook({
        playSubset: data,
        shouldClosePreviewTab: true,
      });
      return;
    }

    const presentation = await getPresentation({
      organizationId,
      id: data.presentations[0].presentationId,
    }).unwrap();
    const analyticsSessionId = window.dsDesktop.analyticsClient.start({
      presentationId: String(presentation.id),
      presentationType: PresentationTypeConst.demo,
      resourceType: 'managed_presentation',
      forClient: presentation.forClient,
    });

    const presentations = preparePresentationsData({
      presentations: [presentation],
      demosList: [entity] as (IDemo | DemoOverlay)[],
      analyticsSessionId,
    });
    onPresent?.(presentations[0]);
    const activeGlobalTab = getActiveGlobalTab();

    await closeGroupTabs(activeGlobalTab);
    await openDemoPresentation(
      presentation,
      entity as IDemo | DemoOverlay,
      analyticsSessionId
    );
  };

  const createPlay_ = async (entity: Entity) => {
    const forClient = DEMO_LINK_DEFAULT_FOR_CLIENT;
    const assets = getAssets();

    const speakerNotes: Record<string, string> = {};
    const noteFetches = assets
      .filter((asset) => asset.assetType !== 'externalUrl')
      .map((asset) =>
        getNotes({
          organizationId,
          entityId: String(asset.assetId),
          entityType: asset.assetType as NoteEntityType,
        })
      );
    await Promise.all(noteFetches).then((response) => {
      response.forEach((resolved) => {
        if (resolved.originalArgs?.entityId && resolved.data?.[0]?.content) {
          speakerNotes[resolved.originalArgs.entityId] =
            resolved.data?.[0]?.content;
        }
      });
      return;
    });

    const createPresentationAssets = assets.map(
      ({ assetRevisionId, entity }) => {
        const isTourType = getEntityType(entity) === 'tour';
        return {
          assetId: entity.id,
          assetType: getEntityType(entity),
          assetRevisionId: Number(assetRevisionId),
          creationData: {
            forClient,
            notes: speakerNotes[entity.id.toString()] ?? '',
            ...(isTourType
              ? ({
                  tourId: entity.id,
                } as Pick<TourCreationData, 'tourId'>)
              : ({
                  demoId: entity.id,
                  variables: [],
                } as Pick<DemoCreationData, 'demoId' | 'variables'>)),
          },
        };
      }
    );

    return createPlay({
      organizationId,
      workspaceId,
      assetId: Number(entity.id),
      assetType: getAssetType(entity),
      settings: { forClient },
      assets: createPresentationAssets,
    }).unwrap();
  };

  useEffect(() => {
    if (!withGiveADemo) {
      createPlay_(entity)
        .then(handleOnPresent)
        .catch((error) => {
          console.error('Error creating presentation', error);
          notify.error('Error creating presentation');
        });
    }
  }, []);

  return !withGiveADemo ? null : (
    <ModalWrapper isVisible>
      <GiveADemoFlowModal
        withNotificationOnPresent={withNotificationOnPresent}
        open={open}
        asset={{
          id: String(entity.id),
          name: getEntityTitle(entity),
          type: getAssetType(entity),
          assets: getAssets(),
        }}
        onClose={() => {
          trackEvent(
            AnalyticsTrackEventName.DEMO_CLICK_CANCEL_PRESENTATION_DEMO_MODAL,
            {
              location: 'all_demos_demo_tile',
              demoId: entity.id,
            }
          );
          onClose?.();
        }}
        onPresent={handleOnPresent}
        onSave={(data) => {
          trackEvent(
            AnalyticsTrackEventName.DEMO_CLICK_PLAY_LATER_PRESENTATION,
            {
              location: 'give_a_demo_modal',
            }
          );
          onSave?.(data);
        }}
        filesServiceUrl={settings.FILES_SERVICE_URL}
        onLoadingChange={onLoadingChange}
        hidePlayLater={hidePlayLater}
        workspaceId={workspaceId}
      />
    </ModalWrapper>
  );
};
